import { classifyUnsafeSituation, GIUSP_CLASSIFICATION } from '../../utils/gas/giuspEngine';
import { analyzeFlueGas, validateWorkingPressureDrop } from '../../utils/gas/gasCalculations';

/**
 * GIUSP classification result used to drive warning/banner decisions.
 * Exported for the Form/PDF layers to reflect the live situation status.
 */
export const computeGiuspFlags = (formData) => {
  if (!formData?.appliances) {
    return { situation: null, flags: [] };
  }

  const failedRisk = formData.appliances.filter(
    (a) =>
      a.visualCondition === 'Fail' ||
      a.smokeMatchSpillage === 'Fail' ||
      a.smokeMatchVentilation === 'Fail' ||
      a.applianceSafeToUse === 'No' ||
      a.adequateVentilation === 'No'
  );

  // Evaluate flue gas analysis each appliance for elevated CO ratio
  const hasElevatedCo = formData.appliances.some((a) => {
    if (!Number(a.coPpm) || !Number(a.co2Pct)) return false;
    const analysis = analyzeFlueGas({ coPpm: a.coPpm, co2Pct: a.co2Pct });
    return !analysis.compliant;
  });

  const situation = classifyUnsafeSituation({
    gasEscape: false,
    spillage: failedRisk.some((a) => a.smokeMatchSpillage === 'Fail'),
    highCo: hasElevatedCo,
    applianceDamaged: failedRisk.length > 0 && !hasElevatedCo,
    poorVentilation: formData.appliances.some((a) => a.adequateVentilation === 'No')
  });

  const flags = failedRisk.map((a) => ({
    location: a.location || 'Unknown location',
    reason: ['visualCondition', 'smokeMatchSpillage', 'smokeMatchVentilation', 'adequateVentilation']
      .filter((key) => a[key] === 'Fail' || a[key] === 'No')
      .map((key) => {
        const labels = {
          visualCondition: 'Visual condition FAIL',
          smokeMatchSpillage: 'Spillage test FAIL',
          smokeMatchVentilation: 'Ventilation smoke match FAIL',
          adequateVentilation: 'Inadequate ventilation'
        };
        return labels[key];
      })
  }));

  return { situation, flags };
};

export default {
  validate(formData) {
    const errors = {};

    // Inspector validation
    if (!formData.inspector?.name?.trim()) {
      errors.inspector_name = 'Inspector name is required';
    }
    if (!formData.inspector?.address?.trim()) {
      errors.inspector_address = 'Inspector address is required';
    }
    if (!formData.inspector?.postCode?.trim()) {
      errors.inspector_postCode = 'Inspector post code is required';
    }
    if (!formData.inspector?.tel?.trim()) {
      errors.inspector_tel = 'Inspector telephone is required';
    }
    if (!formData.inspector?.gasRegNumber?.trim()) {
      errors.inspector_gasRegNumber = 'Gas Safe Register Number is required';
    }

    // Inspection Address validation
    if (!formData.inspectionAddress?.address?.trim()) {
      errors.inspectionAddress_address = 'Inspection address is required';
    }
    if (!formData.inspectionAddress?.postCode?.trim()) {
      errors.inspectionAddress_postCode = 'Inspection post code is required';
    }

    // Landlord validation
    if (!formData.landlord?.name?.trim()) {
      errors.landlord_name = 'Landlord name is required';
    }
    if (!formData.landlord?.address?.trim()) {
      errors.landlord_address = 'Landlord address is required';
    }
    if (!formData.landlord?.postCode?.trim()) {
      errors.landlord_postCode = 'Landlord post code is required';
    }

    // Appliances validation
    if (!formData.appliances || formData.appliances.length === 0) {
      errors.appliances = 'At least one appliance is required';
    } else {
      formData.appliances.forEach((appliance, index) => {
        if (!appliance.location?.trim()) {
          errors[`appliances_location_${index}`] = `Appliance ${index + 1}: Location is required`;
        }
        if (!appliance.type) {
          errors[`appliances_type_${index}`] = `Appliance ${index + 1}: Type is required`;
        }
        if (!appliance.inspected) {
          errors[`appliances_inspected_${index}`] = `Appliance ${index + 1}: Inspected is required`;
        }
        if (!appliance.applianceSafeToUse) {
          errors[`appliances_applianceSafeToUse_${index}`] = `Appliance ${index + 1}: Appliance Safe to Use is required`;
        }
      });
    }

    const giusp = computeGiuspFlags(formData);
    if (
      giusp.situation?.classification === GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS ||
      giusp.situation?.classification === GIUSP_CLASSIFICATION.AT_RISK
    ) {
      if (!formData.soundnessTest?.warningNoticeIssued || formData.soundnessTest.warningNoticeIssued === 'No') {
        errors.soundnessTest_warningNoticeIssued =
          'A warning notice must be recorded when an unsafe situation (ID or AR) is present.';
      }
    }

    // Combustion analysis completeness
    formData.appliances.forEach((appliance, index) => {
      if (appliance.combustionAnalysisDone === 'Yes') {
        if (!appliance.coPpm) {
          errors[`appliances_coPpm_${index}`] = `Appliance ${index + 1}: CO reading is required when combustion analysis performed`;
        }
        if (!appliance.co2Pct) {
          errors[`appliances_co2Pct_${index}`] = `Appliance ${index + 1}: CO₂ reading is required`;
        }
      }
    });

    // Working pressure drop validation if readings provided
    const drop = validateWorkingPressureDrop(
      formData.appliances?.[0]?.meterPressure,
      formData.appliances?.[0]?.appliancePressure
    );
    if (!drop.acceptable && drop.appliancePressureMbar > 0) {
      errors.appliances_pressureDrop = drop.issues.join(' ');
    }

    // Soundness Test validation
    if (!formData.soundnessTest?.result) {
      errors.soundnessTest_result = 'Soundness test result is required';
    }

    // Certification validation
    if (!formData.certification?.numberOfAppliances) {
      errors.certification_numberOfAppliances = 'Number of appliances is required';
    }
    if (!formData.certification?.nextCheckMonths) {
      errors.certification_nextCheckMonths = 'Next check period is required';
    }
    if (!formData.certification?.inspectionDate) {
      errors.certification_inspectionDate = 'Inspection date is required';
    }
    if (!formData.certification?.certificateNumber?.trim()) {
      errors.certification_certificateNumber = 'Certificate number is required';
    }
    if (!formData.certification?.engineerSignature) {
      errors.certification_engineerSignature = 'Engineer signature is required';
    }
    if (!formData.certification?.engineerPrintName?.trim()) {
      errors.certification_engineerPrintName = 'Engineer printed name is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};